import { useEffect, useState } from 'react';
import { SideNav, SideNavCategory, SideNavItem, SideNavSection } from '@airbus/components-react';
import { useUserContextApi } from '../../context/UserContext';
import styles from './SideNavigation.module.css';
import headerStyles from './header.module.css';
import { useAllScreensContext } from '../../context/AllScreensDetailsContext';
import { useNavigate } from 'react-router';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isPspManagementHidden } from '../../utils/navigationVisibilityUtils';

type Props = {
  showSideNav: boolean;
  setShowSideNav: (open: boolean) => void;
};

function SideNavigation({ showSideNav, setShowSideNav }: Readonly<Props>) {
  const navigate = useNavigate();
  const [screenDetails, setScreenDetails] = useState([]);
  const { menuGroup } = useAllScreensContext();
  const { accessControlDetails, acceptDisclaimer } = useUserContextApi();
  const pspEnabled = useFeatureFlagEnabled('newPspManagement') === true;

  const [openCategoryId, setOpenCategoryId] = useState<null | number>(null);
  const [openSubCategoryIds, setOpenSubCategoryIds] = useState<Record<string | number, any>>({});

  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window === 'undefined' ? true : window.innerWidth >= 1350,
  );

  const getAccessibleScreens = (accessControlDetails: any[]) =>
    accessControlDetails.map((item) => item.mbdScreen.screenInternalName);

  const filterSubChildMenu = (sub: any, accessibleScreens: any[]) => {
    return Array.isArray(sub.subChildMenu)
      ? sub.subChildMenu.filter((child: any) => {
          if (!accessibleScreens.includes(child.appNameId)) {
            return false;
          }

          if (isPspManagementHidden(child.appNameId, pspEnabled)) {
            return false;
          }

          return true;
        })
      : [];
  };

  const filterSubMenu = (group: any, accessibleScreens: any[]) => {
    return group.subMenu
      .map((sub: any) => {
        const filteredSubChild = filterSubChildMenu(sub, accessibleScreens);
        return { ...sub, subChildMenu: filteredSubChild };
      })
      .filter((sub: any) => {
        if (isPspManagementHidden(sub.appNameId, pspEnabled)) {
          return false;
        }

        if (accessibleScreens.includes(sub.appNameId)) {
          return true;
        }

        return (sub.subChildMenu?.length ?? 0) > 0;
      });
  };

  const filterMenuGroup = (menuGroup: any[], accessibleScreens: any[]) => {
    return menuGroup
      .map((group: any) => {
        const filteredSubMenu = filterSubMenu(group, accessibleScreens);
        return filteredSubMenu.length > 0 ? { ...group, subMenu: filteredSubMenu } : null;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (!menuGroup || !accessControlDetails?.length) return;

    const accessibleScreens = getAccessibleScreens(accessControlDetails);
    const filteredScreens = filterMenuGroup(menuGroup, accessibleScreens);

    setScreenDetails(filteredScreens);
  }, [accessControlDetails, menuGroup, pspEnabled]);

  const navigateTo = (appName: string, screenName: number) => {
    const route = appName === 'NPI Tool Administration' ? `/admin` : `/mbdcp`;
    navigate(`${route}/${screenName}`);
  };

  const handleCategoryToggle = (id: any) => {
    setOpenCategoryId((prevId) => {
      const isSame = prevId === id;
      if (isSame) {
        setOpenSubCategoryIds({});
        return null;
      }
      setOpenSubCategoryIds({});
      return id;
    });
  };

  const handleSubCategoryToggle = (parentId: any, subId: any) => {
    setOpenSubCategoryIds((prev) => ({
      ...prev,
      [parentId]: prev[parentId] === subId ? null : subId,
    }));
  };

  const handleClose = () => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove(headerStyles.drawerOpen);
    }
    const CLOSE_MS = 280;
    setTimeout(() => setShowSideNav(false), CLOSE_MS);
    setOpenCategoryId(null);
    setOpenSubCategoryIds({});
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = () => {
      const nowDesktop = window.innerWidth >= 1350;
      if (nowDesktop) {
        setIsDesktop(true);
        setShowSideNav(false);
        document.body.classList.remove(headerStyles.drawerOpen);
      } else {
        setIsDesktop(false);
      }
    };

    onResize();

    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, [setShowSideNav]);

  if (isDesktop || !accessControlDetails?.length || !acceptDisclaimer) {
    return null;
  }

  const renderChildItems = (subMenu: any) => {
    return subMenu.subChildMenu
      .sort((a: any, b: any) => a.maxSortOrder - b.maxSortOrder)
      .map((child: any) => (
        <SideNavItem
          key={child.id}
          onSelected={() => navigateTo(child.appName, child.appNameId)}
          data-testid='sidenav'
        >
          {child.screenName}
        </SideNavItem>
      ));
  };

  const renderSubMenu = (group: any, subMenu: any) => {
    if (subMenu.subChildMenu.length) {
      return (
        <SideNavCategory
          key={subMenu.id}
          label={subMenu.screenName}
          open={openSubCategoryIds[group.id] === subMenu.id}
          onSelected={() => handleSubCategoryToggle(group.id, subMenu.id)}
          data-testid={`subcategory-${subMenu.appNameId}`}
        >
          {renderChildItems(subMenu)}
        </SideNavCategory>
      );
    }

    return (
      <SideNavItem
        key={subMenu.id}
        onSelected={() => navigateTo(subMenu.appName, subMenu.appNameId)}
        data-testid='sidenav'
      >
        {subMenu.screenName}
      </SideNavItem>
    );
  };

  const renderGroup = (group: any) => {
    return (
      <SideNavCategory
        key={group.id}
        label={group.appName}
        open={openCategoryId === group.id}
        onSelected={() => handleCategoryToggle(group.id)}
        data-testid={`category-${group.appNameId}`}
      >
        {group.subMenu
          .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
          .map((subMenu: any) => renderSubMenu(group, subMenu))}
      </SideNavCategory>
    );
  };

  return (
    <SideNav
      theme='light'
      open={showSideNav}
      onClose={handleClose}
      aria-label='Main Navigation'
      className={styles.sideNavigationPadding}
    >
      <SideNavSection>
        {screenDetails?.map((group: any) => renderGroup(group))}
      </SideNavSection>
    </SideNav>
  );
}
export default SideNavigation;
